import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePlus2, Loader2, Mail, Pencil, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const STATUS_VARIANTS = {
  draft: "border-zinc-600 text-zinc-400",
  active: "border-emerald-500/40 text-emerald-300",
  archived: "border-rose-500/40 text-rose-300",
};

const EMPTY_TEMPLATE = {
  slug: "",
  name: "",
  channel: "email",
  description: "",
  subject_a: "",
  subject_b: "",
  preheader: "",
  html_body: "",
  plain_body: "",
  sms_body: "",
  status: "draft",
  tags: [],
};

function TemplateEditor({ template, onSaved, onClose }) {
  const [form, setForm] = useState(template || EMPTY_TEMPLATE);
  const [busy, setBusy] = useState(false);
  const [previewContext, setPreviewContext] = useState('{"customer_name":"Matthew","portal_url":"https://blackrdg.com/members"}');
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState("compose");
  const isNew = !template?.slug;

  const save = async () => {
    if (!form.slug) { toast.error("Slug required"); return; }
    if (!form.name) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const updated = await crmApi.upsertTemplate(form.slug, {
        name: form.name,
        channel: form.channel,
        description: form.description,
        subject_a: form.subject_a,
        subject_b: form.subject_b,
        preheader: form.preheader,
        html_body: form.html_body,
        plain_body: form.plain_body,
        sms_body: form.sms_body,
        status: form.status,
        tags: form.tags,
      });
      toast.success(`Template “${updated.name}” saved`);
      onSaved?.();
      onClose();
    } catch (exc) {
      toast.error(exc.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    let ctx = {};
    try { ctx = JSON.parse(previewContext); } catch (_e) { toast.error("Context must be valid JSON"); return; }
    try {
      const data = await crmApi.previewTemplate(form.slug, ctx);
      setPreview(data);
      setTab("preview");
    } catch (exc) {
      toast.error(exc.message || "Preview failed");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-black border-zinc-800 text-white max-w-4xl" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? "New template" : `Edit · ${form.name}`}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 border-b border-zinc-800 -mt-1">
          {[
            { key: "compose", label: "Compose" },
            { key: "preview", label: "Preview" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wide border-b-2 ${tab === t.key ? "border-amber-400 text-amber-300" : "border-transparent text-zinc-400 hover:text-white"}`}
              data-testid={`tpl-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "compose" && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-zinc-400">Slug (lowercase, no spaces)</Label>
              <Input value={form.slug} disabled={!isNew} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-") })} className="bg-zinc-950 border-zinc-700 text-white font-mono" data-testid="tpl-slug" placeholder="welcome_message" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-zinc-400">Display name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="tpl-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-zinc-400">Channel</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="tpl-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS (scaffolded)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-zinc-400">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="tpl-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">draft</SelectItem>
                  <SelectItem value="active">active</SelectItem>
                  <SelectItem value="archived">archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-zinc-400">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" placeholder="What is this template for?" />
            </div>
            {form.channel === "email" ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-zinc-400">Subject (variant A)</Label>
                  <Input value={form.subject_a} onChange={(e) => setForm({ ...form, subject_a: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="tpl-subject-a" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-zinc-400">Subject (variant B — optional)</Label>
                  <Input value={form.subject_b} onChange={(e) => setForm({ ...form, subject_b: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="tpl-subject-b" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-zinc-400">Preheader</Label>
                  <Input value={form.preheader} onChange={(e) => setForm({ ...form, preheader: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-zinc-400">HTML body</Label>
                  <Textarea rows={10} value={form.html_body} onChange={(e) => setForm({ ...form, html_body: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white font-mono text-xs" data-testid="tpl-html" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-zinc-400">Plain-text fallback</Label>
                  <Textarea rows={4} value={form.plain_body} onChange={(e) => setForm({ ...form, plain_body: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white text-xs" />
                </div>
              </>
            ) : (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-zinc-400">SMS body (max ~160 chars)</Label>
                <Textarea rows={4} value={form.sms_body} onChange={(e) => setForm({ ...form, sms_body: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" />
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-zinc-400">Variables used (auto-detected on save)</Label>
              <div className="flex flex-wrap gap-1 text-[11px] text-zinc-400 font-mono">
                {(form.variables_used || []).map((v) => <span key={v} className="px-1.5 py-0.5 rounded border border-zinc-700">{v}</span>)}
                {(form.variables_used || []).length === 0 && <span className="text-zinc-500">(none yet)</span>}
              </div>
            </div>
          </div>
        )}

        {tab === "preview" && (
          <div className="space-y-3" data-testid="tpl-preview-pane">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-zinc-400">Preview context (JSON)</Label>
              <Textarea rows={3} value={previewContext} onChange={(e) => setPreviewContext(e.target.value)} className="bg-zinc-950 border-zinc-700 text-white font-mono text-xs" data-testid="tpl-preview-ctx" />
              <Button size="sm" onClick={runPreview} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="tpl-preview-run">
                <Sparkles className="h-3.5 w-3.5 mr-1" /> Render preview
              </Button>
            </div>
            {preview ? (
              <div className="space-y-3">
                <div className="rounded border border-zinc-800 p-3 bg-zinc-950">
                  <div className="text-[11px] uppercase text-zinc-500">Subject A</div>
                  <div className="text-white">{preview.subject_a || "(empty)"}</div>
                  {preview.subject_b && (<><div className="text-[11px] uppercase text-zinc-500 mt-1">Subject B</div><div className="text-white">{preview.subject_b}</div></>)}
                </div>
                <div className="rounded border border-zinc-800 p-3 bg-white text-zinc-900 overflow-auto" style={{maxHeight: 400}}>
                  <div dangerouslySetInnerHTML={{ __html: preview.html_body || preview.plain_body || "(no body)" }} />
                </div>
              </div>
            ) : (
              <p className="text-xs text-zinc-500">Click <em>Render preview</em> to see how it&rsquo;ll look with the context above.</p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
          <Button variant="ghost" onClick={onClose} className="text-zinc-300" disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="tpl-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save template</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TemplatesView() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        const list = await crmApi.listTemplates(params);
        if (active) setTemplates(list || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load templates");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [statusFilter, refreshCounter]);

  const reload = useCallback(() => setRefreshCounter((c) => c + 1), []);
  const rows = useMemo(() => templates, [templates]);

  return (
    <div className="space-y-6" data-testid="owner-templates">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Mail className="h-5 w-5 text-amber-300" /> Templates</h1>
          <p className="text-sm text-zinc-400 mt-1">Reusable email and SMS templates with variable substitution and A/B subjects.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-32 bg-zinc-950 border-zinc-700 text-white" data-testid="tpl-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" className="bg-amber-500 text-black hover:bg-amber-400" onClick={() => setEditing(null) || setEditing({})} data-testid="new-template-btn">
            <FilePlus2 className="h-4 w-4 mr-1" /> New template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <Card className="bg-black/40 border-zinc-800"><CardContent className="py-10 text-center text-sm text-zinc-400">No templates match this filter.</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="templates-grid">
          {rows.map((t) => (
            <Card key={t.id} className="bg-black/40 border-zinc-800 hover:border-amber-500/40 transition-colors" data-testid={`template-card-${t.slug}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-white font-semibold">{t.name}</div>
                    <div className="text-[11px] font-mono text-zinc-500">{t.slug}</div>
                  </div>
                  <Badge variant="outline" className={STATUS_VARIANTS[t.status] || ""}>{t.status}</Badge>
                </div>
                <div className="text-xs text-zinc-400 line-clamp-2">{t.description || "—"}</div>
                <div className="text-[11px] text-zinc-500">
                  channel: {t.channel} {t.is_system && <span className="ml-2 text-amber-300">system</span>}
                </div>
                {(t.variables_used || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 text-[10px] font-mono text-zinc-400 pt-1">
                    {t.variables_used.slice(0, 6).map((v) => <span key={v} className="px-1.5 py-0.5 rounded border border-zinc-700">{v}</span>)}
                    {t.variables_used.length > 6 && <span className="text-zinc-500">+{t.variables_used.length - 6}</span>}
                  </div>
                )}
                <div className="flex justify-end pt-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(t)} className="text-amber-300 hover:bg-amber-500/10" data-testid={`edit-template-${t.slug}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing !== null && (
        <TemplateEditor
          template={editing.slug ? editing : null}
          onSaved={reload}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
